import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule, TableLazyLoadEvent } from 'primeng/table';
import { Select } from 'primeng/select';
import { InputText } from 'primeng/inputtext';
import { DatePicker } from 'primeng/datepicker';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { catchError, of, Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { AuditCategory, AuditLog, AuditLogQuery } from '../../models/audit-log.model';
import { AuditLogService } from '../../services/audit-log.service';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';

@Component({
  selector: 'app-audit-logs',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Select,
    InputText,
    DatePicker,
    Tag,
    Tooltip
  ],
  templateUrl: './audit-logs.component.html',
  styleUrl: './audit-logs.component.scss'
})
export class AuditLogsComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);

  logs: AuditLog[] = [];
  loading = false;
  totalRecords = 0;

  // Paging state (server-side / lazy).
  rows = 25;
  first = 0;
  rowsPerPageOptions: number[] = [10, 25, 50, 100];

  // Filters. The page opens focused on Master Data (Settings changes); the user can
  // switch to any other category or clear it to see everything.
  categories: AuditCategory[] = [];
  selectedCategory: string | null = 'Master Data';
  entityNames: string[] = [];
  selectedEntity: string | null = null;
  selectedAction: string | null = null;
  changedByFilter = '';
  selectedSource: string | null = null; // null=all, 'System'=automated, '!System'=user actions
  fromDate: Date | null = null;
  toDate: Date | null = null;
  searchText = '';

  actionOptions: { label: string; value: string | null }[] = [
    { label: 'All actions', value: null },
    { label: 'Created', value: 'Created' },
    { label: 'Updated', value: 'Updated' },
    { label: 'Deleted', value: 'Deleted' }
  ];

  sourceOptions: { label: string; value: string | null }[] = [
    { label: 'All sources', value: null },
    { label: 'User actions', value: '!System' },
    { label: 'System (automated)', value: 'System' }
  ];

  // Per-action counts for the current page (stats cards).
  get createdCount(): number { return this.logs.filter(l => l.action === 'Created').length; }
  get updatedCount(): number { return this.logs.filter(l => l.action === 'Updated').length; }
  get deletedCount(): number { return this.logs.filter(l => l.action === 'Deleted').length; }

  constructor(
    private auditLogService: AuditLogService,
    public pageHeaderService: PageHeaderSearchActionService
  ) {}

  // Debounces the free-text "Changed By" input so we don't reload per keystroke.
  private changedBy$ = new Subject<string>();

  ngOnInit(): void {
    // Wire the global header search box to the log search filter.
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search audit logs...');
    this.pageHeaderService.searchText
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((text) => {
        this.searchText = text ?? '';
        this.first = 0;
        this.load();
      });

    // Debounce the "Changed By" text box: reload only after the user pauses typing.
    this.changedBy$
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.first = 0;
        this.load();
      });

    this.loadCategories();
    this.loadEntityNames();
    this.load();
  }

  ngOnDestroy(): void {
    this.pageHeaderService.setCanShowFilterToggle(false);
    this.pageHeaderService.setPlaceholder('');
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    this.first = event.first ?? 0;
    this.rows = event.rows ?? this.rows;
    this.load();
  }

  onFilterChange(): void {
    // Any filter change resets to the first page.
    this.first = 0;
    this.load();
  }

  // "Changed By" free-text: push to the debounced stream instead of reloading immediately.
  onChangedByInput(): void {
    this.changedBy$.next(this.changedByFilter);
  }

  // Changing the main category clears the entity sub-filter so it can't point at an
  // entity outside the newly-selected group.
  onCategoryChange(): void {
    this.selectedEntity = null;
    this.first = 0;
    this.load();
  }

  clearFilters(): void {
    this.selectedCategory = null;
    this.selectedEntity = null;
    this.selectedAction = null;
    this.changedByFilter = '';
    this.selectedSource = null;
    this.fromDate = null;
    this.toDate = null;
    this.first = 0;
    this.load();
  }

  hasActiveFilters(): boolean {
    return !!(
      this.selectedCategory || this.selectedEntity || this.selectedAction ||
      this.changedByFilter || this.selectedSource || this.fromDate || this.toDate
    );
  }

  private load(): void {
    this.loading = true;
    // A typed "Changed By" name takes precedence; otherwise use the Source dropdown
    // (System / User actions) which maps to the same backend changedBy filter via tokens.
    const changedBy = this.changedByFilter?.trim() || this.selectedSource || undefined;
    const query: AuditLogQuery = {
      pageNumber: Math.floor(this.first / this.rows) + 1,
      pageSize: this.rows,
      category: this.selectedCategory ?? undefined,
      entityName: this.selectedEntity ?? undefined,
      action: this.selectedAction ?? undefined,
      changedBy,
      fromDate: this.fromDate ? this.toIsoDate(this.fromDate) : undefined,
      toDate: this.toDate ? this.toIsoDate(this.toDate) : undefined,
      search: this.searchText?.trim() || undefined
    };

    this.auditLogService
      .query(query)
      .pipe(
        catchError(() => of({ items: [], totalCount: 0, pageNumber: 1, pageSize: this.rows })),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result) => {
        this.logs = result.items;
        this.totalRecords = result.totalCount;
        this.loading = false;
      });
  }

  private loadEntityNames(): void {
    this.auditLogService
      .getEntityNames()
      .pipe(catchError(() => of([])), takeUntilDestroyed(this.destroyRef))
      .subscribe((names) => (this.entityNames = names));
  }

  private loadCategories(): void {
    this.auditLogService
      .getCategories()
      .pipe(catchError(() => of([])), takeUntilDestroyed(this.destroyRef))
      .subscribe((cats) => (this.categories = cats));
  }

  get categoryOptions(): { label: string; value: string | null }[] {
    return [{ label: 'All categories', value: null }, ...this.categories.map((c) => ({ label: c.name, value: c.name }))];
  }

  // Entity sub-filter: scoped to the selected category when one is chosen, otherwise
  // shows every entity that actually appears in the log.
  get entityOptions(): { label: string; value: string | null }[] {
    let names = this.entityNames;
    if (this.selectedCategory) {
      const cat = this.categories.find((c) => c.name === this.selectedCategory);
      if (cat) {
        names = cat.entities.filter((e) => this.entityNames.length === 0 || this.entityNames.includes(e));
        if (names.length === 0) {
          names = cat.entities; // fall back to the full category list if nothing logged yet
        }
      }
    }
    return [{ label: 'All entities', value: null }, ...names.map((n) => ({ label: n, value: n }))];
  }

  // Short, simple summary. Created/Deleted just say so (the entity + name are already in
  // the Entity column, and the full field list is one click away in the expand row).
  // Updated shows the specific change(s), concisely.
  changeSummary(log: AuditLog): string {
    if (log.action === 'Created') {
      return 'Created';
    }
    if (log.action === 'Deleted') {
      return 'Deleted';
    }

    // Updated — describe the actual field change(s).
    if (!log.changes || log.changes.length === 0) {
      return 'Updated';
    }

    // Timesheet lifecycle → one natural sentence ("Submitted on …", "Approved by …").
    if (log.entityName === 'Timesheet') {
      const lifecycle = this.timesheetLifecycle(log);
      if (lifecycle) {
        return lifecycle;
      }
    }

    // Show a status (Activated/Deactivated) change FIRST so it's never buried.
    const statusChange = log.changes.find((c) => this.statusPhrase(c));
    const rest = log.changes.filter((c) => c !== statusChange);
    const ordered = statusChange ? [statusChange, ...rest] : log.changes;

    const parts = ordered.slice(0, 2).map((c) => {
      // "Email Sent" datetime → natural "Email sent successfully on <date>".
      if (c.field === 'Email Sent' && c.new) {
        return `Email sent successfully on ${c.new}`;
      }

      const status = this.statusPhrase(c);
      if (status) {
        return status;
      }

      const oldV = this.readable(c.old);
      const newV = this.readable(c.new);
      const hasOld = oldV !== null;
      const hasNew = newV !== null;

      if (!hasOld && hasNew) {
        return `${c.field} set to ${newV}`;
      }
      if (hasOld && !hasNew) {
        return `${c.field} removed`;
      }
      if (hasOld && hasNew) {
        return `${c.field} → ${newV}`;
      }
      return `${c.field} updated`;
    });

    const more = log.changes.length > 2 ? ` +${log.changes.length - 2} more` : '';
    return parts.join('; ') + more;
  }

  // Natural sentence for a Timesheet lifecycle change (submit/approve/revert), or null.
  // Entity column already shows "Employee — Month Year".
  private timesheetLifecycle(log: AuditLog): string | null {
    const get = (field: string) => log.changes.find((c) => c.field === field);
    const isYes = (v: string | null | undefined) => v === 'Yes' || v === 'True' || v === 'true';

    const submitted = get('Is Submitted');
    const approved = get('Is Approved');
    const who = log.entityDisplay ? log.entityDisplay.split('—')[0].trim() : 'Timesheet';
    const when = get('Submitted Date')?.new || get('Approved Date')?.new;

    if (approved) {
      if (isYes(approved.new)) {
        const by = get('Approved By')?.new;
        return `Approved${by ? ` by ${by}` : ''}${when ? ` on ${when}` : ''}`;
      }
      return 'Approval reverted';
    }
    if (submitted) {
      return isYes(submitted.new)
        ? `${who} submitted${when ? ` on ${when}` : ''}`
        : 'Reverted to draft';
    }
    return null;
  }

  // Turn a boolean active-flag change into a natural action phrase, or null if not applicable.
  // "Is Default" is the active/inactive flag for onboarding/offboarding task templates.
  private statusPhrase(c: { field: string; old: string | null; new: string | null }): string | null {
    if (c.field !== 'Is Active' && c.field !== 'Is Default') {
      return null;
    }
    const isYes = (v: string | null) => v === 'Yes' || v === 'True' || v === 'true';
    if (isYes(c.new) && !isYes(c.old)) {
      return 'Activated';
    }
    if (!isYes(c.new) && isYes(c.old)) {
      return 'Deactivated';
    }
    // Created/Deleted case where only "new" is present.
    if (c.old === null || c.old === undefined || c.old === '') {
      return isYes(c.new) ? 'Set as Active' : 'Set as Inactive';
    }
    return null;
  }

  private static readonly GUID_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  // Clean a stored value into readable text: trim, normalize non-breaking spaces, treat
  // blanks as empty, replace raw GUIDs with a friendly placeholder, and space out
  // CamelCase enum-style values ("ProjectMilestone" -> "Project Milestone").
  private readable(value: string | null): string | null {
    if (value === null || value === undefined) {
      return null;
    }
    // Normalize non-breaking spaces and collapse whitespace.
    let v = value.replace(/ /g, ' ').replace(/\s+/g, ' ').trim();
    if (v === '') {
      return null;
    }
    if (AuditLogsComponent.GUID_RE.test(v)) {
      return '(reference)';
    }
    // Space out single-word CamelCase enum values (no spaces, has an internal capital).
    if (/^[A-Za-z]+$/.test(v) && /[a-z][A-Z]/.test(v)) {
      v = v.replace(/([a-z])([A-Z])/g, '$1 $2');
    }
    return v;
  }

  // Readable cell value for the expansion table: blank values shown as a friendly dash.
  displayValue(value: string | null): string {
    return this.readable(value) ?? '—';
  }

  // Created/Deleted rows have no meaningful "before vs after" — show a single Value column.
  isTwoColumn(log: AuditLog): boolean {
    return log.action === 'Created' || log.action === 'Deleted';
  }

  // The single value to show in the 2-column view: the new value for Created, the old for Deleted.
  singleValue(log: AuditLog, change: { old: string | null; new: string | null }): string {
    return this.displayValue(log.action === 'Deleted' ? change.old : change.new);
  }

  actionSeverity(action: string): 'success' | 'info' | 'danger' | 'secondary' {
    switch (action) {
      case 'Created': return 'success';
      case 'Updated': return 'info';
      case 'Deleted': return 'danger';
      default: return 'secondary';
    }
  }

  // Send date-only (no time) so the backend day-range filter is unambiguous.
  private toIsoDate(d: Date): string {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
}
